import { Inject, Injectable } from '@angular/core';
import { FirebaseApp, getApp, initializeApp } from '@angular/fire/app';
import {
  getAuth,
  SAMLAuthProvider,
  signInWithCustomToken,
  signInWithRedirect,
} from '@angular/fire/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import firebase from 'firebase/compat/app';
import { forkJoin, from, Observable, of } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import {
  DEFAULT_INSTANCE_COMPANY_ID,
  EMAIL_FOR_SIGN_IN,
  FIREBASE_APP_INSTANCES_NAMES,
  SAML_LOGIN_COMPANY_ID,
} from 'src/app/shared/constants/storage-keys.constants';
import {
  safeStorageClear,
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet,
} from 'src/app/shared/utils/safe-storage';
import { environment } from 'src/environments/environment';
import { AngularFireAuthFactory } from '../firebase.factory';
import User = firebase.User;
import UserCredential = firebase.auth.UserCredential;

export const ERROR_CODES = {
  'auth/invalid-argument': 'Erro: Um argumento inválido foi fornecido.',
  'auth/wrong-password': 'Erro: Usuário ou senha inválidos',
  'auth/invalid-credential': 'Erro: Usuário ou senha inválidos',
  'auth/invalid-action-code':
    'Esse link de login já expirou ou já foi utilizado. Continue para solicitar um novo link.',
  'auth/invalid-email':
    'E-mail inválido. Verifique se digitou o mesmo e-mail para o qual o link foi enviado.',
  'auth/internal-error': 'Erro: Não foi possível acessar. Tente novamente.',
  TURNSTILE_FAIL:
    'Erro ao validar. Por favor, tente novamente ou fale com o suporte no botão "Ajuda"',
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private router: Router,
    private angularFireAuth: AngularFireAuth,
    @Inject('angularFireAuthFactory')
    private angularFireAuthFactory: AngularFireAuthFactory,
    private localStorageService: LocalStorageService
  ) {}

  getToken(companyId: string | null): Observable<string> {
    return this.getInstance(companyId).idToken;
  }

  getUser(companyId: string | null): Observable<User> {
    return this.getInstance(companyId).user;
  }

  isLoggedIn(companyId: string | null): Observable<boolean> {
    return this.getUser(companyId).pipe(
      map((user) => !!user && !user.isAnonymous)
    );
  }

  getInstance(companyId: string | null): AngularFireAuth {
    const instanceName = safeStorageGet(DEFAULT_INSTANCE_COMPANY_ID);
    if (instanceName !== null && instanceName === companyId) {
      return this.angularFireAuthFactory.getInstance(null);
    }
    return this.angularFireAuthFactory.getInstance(companyId);
  }

  emailPasswordLogin(
    email: string,
    password: string
  ): Observable<UserCredential> {
    return from(
      this.angularFireAuth.signInWithEmailAndPassword(email, password)
    );
  }

  emailLinkComplete(email: string, emailLink: string) {
    // The client SDK will parse the code from the link for you.
    return from(
      this.angularFireAuth.signInWithEmailLink(email, emailLink)
    ).pipe(tap(() => safeStorageRemove(EMAIL_FOR_SIGN_IN)));
  }

  verifyPasswordResetCode(actionCode): Observable<string> {
    return from(this.angularFireAuth.verifyPasswordResetCode(actionCode));
  }

  resetPassword(actionCode: string, newPassword: string): Observable<void> {
    return from(
      this.angularFireAuth.confirmPasswordReset(actionCode, newPassword)
    );
  }

  initializeAndSaveApp(companyId: string): FirebaseApp {
    this.localStorageService.addItemToArray(
      FIREBASE_APP_INSTANCES_NAMES,
      companyId
    );
    return initializeApp(environment.firebase, companyId);
  }

  signInWithRedirect(
    companyId: string,
    tenantId: string,
    providerId: string,
    firstLogin: boolean
  ): Observable<void> {
    if (firstLogin) {
      const app = getApp();
      const auth = getAuth(app);
      safeStorageSet(DEFAULT_INSTANCE_COMPANY_ID, companyId);
      safeStorageSet(SAML_LOGIN_COMPANY_ID, companyId);
      auth.tenantId = tenantId;
      return from(signInWithRedirect(auth, new SAMLAuthProvider(providerId)));
    }

    let app: FirebaseApp;
    if (companyId) {
      app = this.initializeAndSaveApp(companyId);
    } else {
      app = getApp();
    }
    const auth = getAuth(app);
    auth.tenantId = tenantId;
    return from(signInWithRedirect(auth, new SAMLAuthProvider(providerId)));
  }

  signInWithCustomToken(
    companyId: string,
    tenantId: string,
    token: string
  ): Observable<any> {
    const app = this.initializeAndSaveApp(companyId);
    const auth = getAuth(app);
    auth.tenantId = tenantId;
    return from(signInWithCustomToken(auth, token));
  }

  isLoggedInIntoThePlatform(): Observable<boolean> {
    return this.isLoggedIn(null).pipe(
      take(1),
      mergeMap((isLoggedInOnDefaultInstance) => {
        if (isLoggedInOnDefaultInstance) {
          return of(true);
        }

        const names = this.getFirebaseInstanceNames();
        if (!names || names.length === 0) {
          return of(false);
        }

        const results: Observable<boolean>[] = names.map((name) =>
          this.isLoggedIn(name).pipe(take(1))
        );
        return forkJoin(results).pipe(
          map((result: boolean[]) => result.some((value) => value === true))
        );
      })
    );
  }

  getApp(name: string): FirebaseApp {
    let app: FirebaseApp;
    try {
      app = getApp(name);
    } catch {
      app = initializeApp(environment.firebase, name);
    }
    return app;
  }

  async logoutSingleInstance(name: string): Promise<void> {
    const app = this.getApp(name);
    return getAuth(app).signOut();
  }

  async partialLogout(currentCompanyId: string | null): Promise<void> {
    await this.logoutSingleInstance(currentCompanyId);
    this.router.navigate(['/companies']);
  }

  async totalLogout(): Promise<void> {
    const names = this.getFirebaseInstanceNames();
    const promises = names.map((name) => this.logoutSingleInstance(name));
    await Promise.all(promises);
    await getAuth(this.getApp(null)).signOut();
    safeStorageClear();
    try {
      indexedDB.deleteDatabase('firebaseLocalStorageDb');
    } catch {
      // IndexedDB may be unavailable when the browser blocks site data
    }
    Sentry.setUser({ id: null });
    this.router.navigate(['/login']);
  }

  public errorByCode(code: string): string {
    if (ERROR_CODES[code]) {
      return ERROR_CODES[code];
    } else {
      return (
        'Ocorreu um erro! Tente novamente ou fale com o suporte pelo botão de ajuda.' +
        (code ? ' Codigo: ' + code : '')
      );
    }
  }

  private getFirebaseInstanceNames(): string[] {
    return this.localStorageService.getArray(FIREBASE_APP_INSTANCES_NAMES);
  }
}
